import exifr from 'exifr';
import { dateFromFilename } from './filenameDate';

export interface ExifData {
  date: string | null;
  lat: number | null;
  lng: number | null;
}

/**
 * Extract EXIF date and GPS coordinates from an image file.
 * Returns ISO date string and decimal-degree coordinates.
 * Gracefully handles missing or malformed EXIF data.
 */
export async function extractExif(file: File): Promise<ExifData> {
  let date: string | null = null;
  let lat: number | null = null;
  let lng: number | null = null;

  try {
    const parsed = await exifr.parse(file, {
      pick: ['DateTimeOriginal', 'CreateDate', 'GPSLatitude', 'GPSLongitude'],
      // exifr returns GPS as signed decimals by default when using .parse()
    });

    if (parsed) {
      // Resolve date — prefer DateTimeOriginal, fall back to CreateDate
      const rawDate = parsed.DateTimeOriginal ?? parsed.CreateDate;
      if (rawDate instanceof Date && !isNaN(rawDate.getTime())) {
        date = rawDate.toISOString();
      } else if (typeof rawDate === 'string') {
        // EXIF date strings are typically "YYYY:MM:DD HH:MM:SS"
        const normalized = rawDate.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
        const parsedDate = new Date(normalized);
        if (!isNaN(parsedDate.getTime())) {
          date = parsedDate.toISOString();
        }
      }

      // Resolve GPS — exifr returns signed decimal degrees by default
      lat = typeof parsed.GPSLatitude === 'number' && isFinite(parsed.GPSLatitude)
        ? parsed.GPSLatitude
        : null;
      lng = typeof parsed.GPSLongitude === 'number' && isFinite(parsed.GPSLongitude)
        ? parsed.GPSLongitude
        : null;
    }
  } catch {
    // File has no EXIF or is an unsupported format — expected for PNGs, etc.
  }

  // Fallback: Google Photos, WhatsApp, and gallery exports strip EXIF but keep
  // the capture date in the filename (e.g. 20260515_220826.jpg). Recover it so
  // the album can still be ordered chronologically. (GPS cannot be recovered
  // this way — only the date.)
  if (!date) {
    date = dateFromFilename(file.name);
  }

  return { date, lat, lng };
}
